import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import _get from 'lodash/get';
import SVG from 'react-inlinesvg';
import audioIcon from '@lifesize/ux-assets/vectors/svgIcons/headset.svg';
import cancelIcon from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import moreIcon from '@lifesize/ux-assets/vectors/svgIcons/more.svg';
import scheduleIcon from '@lifesize/ux-assets/vectors/svgIcons/calendar.svg';
import videoIcon from '@lifesize/ux-assets/vectors/svgIcons/video.svg';
import DropdownComp from 'components/Dropdown/Dropdown';
import Tooltip from 'components/Tooltip/Tooltip';
import FavoriteStar from 'containers/FavoriteStar/FavoriteStarContainer';
import ChatSearch from 'containers/Chat/ChatSearchContainer';
import classes from 'components/Chat/ChatConversationHeader.module.scss';

const messages = defineMessages({
  audioCall: {
    id: 'components.chat.kebab.audioCall',
    defaultMessage: 'Start with Camera Off'
  },
  schedule: {
    id: 'components.chat.kebab.schedule',
    defaultMessage: 'Schedule'
  },
  unknownUser: {
    id: 'unknownUser',
    defaultMessage: 'Unknown User'
  }
});

class ChatConversationHeader extends React.Component {
  render() {
    const {
      conversation,
      intl,
      isMeeting,
      mode,
      onLeaveConversation,
      onSelectContact,
      openScheduleModal,
      handleAudioCall,
      handleVideoCall,
      isCallConnected,
      isInCallParticipants
    } = this.props;
    const contact = _get(conversation, 'contact', {});
    const contactName =
      _get(contact, 'displayName') || _get(contact, 'name') || intl.formatMessage(messages.unknownUser);

    const kebabItemList = [];
    if (isMeeting) {
      if (!isCallConnected) {
        kebabItemList.push(
          {
            action: () => handleAudioCall(contact),
            icon: audioIcon,
            key: 0,
            label: intl.formatMessage(messages.audioCall)
          },
          {
            action: () => openScheduleModal(contact.id),
            icon: scheduleIcon,
            key: 1,
            label: intl.formatMessage(messages.schedule)
          }
        );
      }
      // CA-2340: Do not show "Chat Participants" in out-of-call kebab anymore.
    } else {
      // not a meeting
      kebabItemList.push({
        action: () => handleAudioCall(contact),
        icon: audioIcon,
        key: 0,
        label: intl.formatMessage(messages.audioCall)
      });
    }

    const kebabButton = () => (
      <button className={classes.chatWindowHeaderButton} type="button" data-testid="kebabButton">
        <SVG src={moreIcon} data-testid="moreIcon" />
      </button>
    );

    const kebabList = () => (
      <div>
        {kebabItemList.map((itemObject) => (
          <div className={classes.kebabOption} key={itemObject.label}>
            <a id={`chatKebab${itemObject.key}`} onClick={itemObject.action} role="button" tabIndex={itemObject.key}>
              <SVG src={itemObject.icon} />
              {itemObject.label}
            </a>
          </div>
        ))}
      </div>
    );

    const displayMode = mode !== 'search' && !_get(conversation, 'groupedMessages.length', 0) ? 'pending' : mode;
    const conversationIsLoading = _get(conversation, 'isLoading', true);

    switch (displayMode) {
      case 'pending':
        return (
          <div className={`${classes.chatWindowHeader} ${classes.newContact}`}>
            {contactName.length > 32 && <Tooltip label={contactName} className={classes.wrapTooltip} />}
            <div className={classes.chatWindowHeaderTitle}>
              {contactName}
              <FavoriteStar
                favClassName={classes.favorites}
                tooltipStyle={{ marginTop: '5px' }}
                contact={_get(this.props.conversation, 'contact', {})}
              />
            </div>
            {!conversationIsLoading ? (
              <button
                className={classes.chatWindowHeaderButton}
                onClick={() => {
                  // select nothing and enter 'search' mode
                  onSelectContact();
                  // remove the empty chat conversation from the ChatList
                  onLeaveConversation(contact);
                }}
                type="button"
              >
                <SVG src={cancelIcon} data-testid="cancelIcon" />
              </button>
            ) : null}
          </div>
        );
      case 'search':
        return (
          <div className={classes.chatWindowHeader}>
            <div className={classes.chatWindowHeaderSearch}>
              <ChatSearch onSelectContact={onSelectContact} />
            </div>
          </div>
        );
      default:
        // current
        return (
          <div className={classes.chatWindowHeader}>
            {contactName.length > 32 && <Tooltip label={contactName} className={classes.wrapTooltip} />}
            <div className={classes.chatWindowHeaderTitle}>
              <span className={classes.name}>{contactName}</span>
              <FavoriteStar
                favClassName={classes.favorites}
                tooltipStyle={{ marginTop: '5px' }}
                contact={_get(this.props.conversation, 'contact', {})}
              />
            </div>
            {contactName && (!isMeeting || !isCallConnected) && !isInCallParticipants ? (
              <button className={classes.chatWindowHeaderButton} onClick={() => handleVideoCall(contact)} type="button">
                <SVG src={videoIcon} />
                <Tooltip
                  fixPosition
                  shift="left"
                  label={<FormattedMessage id="contacts.tooltip.video" defaultMessage="Call with camera on" />}
                />
              </button>
            ) : null}
            {contactName && (isMeeting || !isCallConnected) ? (
              <div className={classes.kebabDropdown}>
                <DropdownComp
                  buttonComponent={kebabButton()}
                  dropdownClassName={classes.kebabDropdownContainer}
                  dropFromTop={82}
                  menuComponent={kebabList()}
                />
              </div>
            ) : null}
          </div>
        );
    }
  }
}

ChatConversationHeader.propTypes = {
  conversation: PT.object,
  intl: PT.object,
  isMeeting: PT.bool,
  mode: PT.string,
  onLeaveConversation: PT.func,
  onSelectContact: PT.func,
  onShowParticipants: PT.func,
  openScheduleModal: PT.func,
  handleAudioCall: PT.func,
  handleVideoCall: PT.func,
  isInCallParticipants: PT.bool,
  isCallConnected: PT.bool,
  isOneTimeMeeting: PT.bool
};

ChatConversationHeader.defaultProps = {
  conversation: { participants: [] },
  mode: 'current'
};

export default injectIntl(ChatConversationHeader);
